<template>
  <div class="bell-sugestoes" ref="bellRef">
    <button class="btn btn-outline-feed position-relative bell-btn" @click="toggleDropdown" title="Notificações">
      <i class="bi bi-bell-fill"></i>
      <span v-if="totalNaoVisualizadas > 0" class="bell-badge">{{ totalNaoVisualizadas > 99 ? '99+' : totalNaoVisualizadas }}</span>
    </button>

    <Teleport to="body">
      <div v-if="dropdownAberto" class="bell-dropdown" :style="dropdownStyle">
        <div class="bell-dropdown-header">
          <strong>Notificações</strong>
          <button v-if="totalNaoVisualizadas > 0" class="btn btn-sm btn-link p-0" @click="marcarTodasVisualizadas" style="font-size:0.75rem;">
            <i class="bi bi-check-all me-1"></i>Marcar todas como lidas
          </button>
        </div>

        <div v-if="notificacoes.length === 0 && !loadingNotificacoes" class="bell-empty">
          <i class="bi bi-bell-slash"></i>
          <span>Nenhuma notificação</span>
        </div>

        <div v-if="loadingNotificacoes" class="bell-loading">
          <div class="spinner-border spinner-border-sm text-feed-primary"></div>
        </div>

        <div v-else class="bell-list">
          <button
            v-for="notif in notificacoes"
            :key="notif.idSugestaoMelhoriaNotificacao"
            class="bell-item"
            :class="{ 'bell-item-nao-lida': !notif.visualizada }"
            @click="abrirNotificacao(notif)"
          >
            <div class="bell-item-icon">
              <i class="bi" :class="getNotifIcon(notif.tipo)"></i>
            </div>
            <div class="bell-item-content">
              <span class="bell-item-mensagem">{{ notif.mensagem }}</span>
              <span class="bell-item-titulo">{{ notif.tituloSugestao }}</span>
              <span class="bell-item-time">{{ timeAgo(notif.tsCriacao) }}</span>
            </div>
            <div v-if="!notif.visualizada" class="bell-item-dot"></div>
          </button>
        </div>

        <div v-if="hasMoreNotificacoes && !loadingNotificacoes" class="bell-ver-mais">
          <button class="btn btn-sm btn-link" @click="carregarMaisNotificacoes">
            <i class="bi bi-arrow-down me-1"></i>Ver mais
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import api from '@/services/api'
import { reservarZIndex, liberarZIndex, BASE_ZINDEX } from '@/composables/useZIndex'

const emit = defineEmits(['abrir-sugestao'])

const bellRef = ref(null)
const dropdownAberto = ref(false)
const notificacoes = ref([])
const loadingNotificacoes = ref(false)
const paginaNotificacoes = ref(1)
const totalNotificacoes = ref(0)
const totalNaoVisualizadas = ref(0)
const hasMoreNotificacoes = ref(false)
const pageSize = 10
let pollingTimer = null

const dropdownStyle = ref({})

// --- Empilhamento (z-index) via registry central ---
const zIndexDropdown = ref(BASE_ZINDEX)
const tokenZIndex = ref(null)

watch(dropdownAberto, (aberto) => {
  if (aberto) {
    const { token, zIndex: z } = reservarZIndex()
    tokenZIndex.value = token
    zIndexDropdown.value = z
  } else {
    liberarZIndex(tokenZIndex.value)
    tokenZIndex.value = null
  }
})

const calcularPosicao = () => {
  if (!bellRef.value) {
    dropdownStyle.value = {}
    return
  }
  const rect = bellRef.value.getBoundingClientRect()

  // Fallback: âncora oculta (display:none) — posiciona centralizado no topo
  if (rect.width === 0 || rect.height === 0) {
    dropdownStyle.value = {
      position: 'fixed',
      top: '60px',
      left: '8px',
      right: '8px',
      zIndex: zIndexDropdown.value
    }
    return
  }

  let top = rect.bottom + 4
  let right = window.innerWidth - rect.right

  // Clamp horizontal: o dropdown (max 360px) não pode sair da viewport
  const dropdownWidth = 360
  const minRight = 8
  const maxRight = window.innerWidth - dropdownWidth - 8
  right = Math.max(minRight, Math.min(right, maxRight))

  // Clamp vertical: se não couber abaixo, posiciona acima
  const dropdownMaxHeight = 480
  if (top + dropdownMaxHeight > window.innerHeight - 8) {
    top = Math.max(8, rect.top - dropdownMaxHeight - 4)
  }

  dropdownStyle.value = {
    position: 'fixed',
    top: top + 'px',
    right: right + 'px',
    zIndex: zIndexDropdown.value
  }
}

const carregarNotificacoes = async (page = 1, append = false) => {
  loadingNotificacoes.value = true
  try {
    const response = await api.get('/SugestoesMelhoria/notificacoes', {
      params: { page, pageSize }
    })
    const data = response.data
    if (append) {
      notificacoes.value = [...notificacoes.value, ...(data.items || [])]
    } else {
      notificacoes.value = data.items || []
    }
    totalNotificacoes.value = data.total || 0
    totalNaoVisualizadas.value = data.totalNaoVisualizadas || 0
    hasMoreNotificacoes.value = data.hasMore || false
    paginaNotificacoes.value = page
  } catch (error) {
    console.error('Erro ao carregar notificações:', error)
  } finally {
    loadingNotificacoes.value = false
  }
}

const carregarMaisNotificacoes = () => {
  if (!hasMoreNotificacoes.value || loadingNotificacoes.value) return
  carregarNotificacoes(paginaNotificacoes.value + 1, true)
}

const toggleDropdown = async () => {
  dropdownAberto.value = !dropdownAberto.value
  if (dropdownAberto.value) {
    calcularPosicao()
    await carregarNotificacoes(1)
  }
}

const abrirNotificacao = async (notif) => {
  // Marca como visualizada
  if (!notif.visualizada) {
    try {
      await api.put(`/SugestoesMelhoria/notificacoes/${notif.idSugestaoMelhoriaNotificacao}/visualizar`)
      notif.visualizada = true
      totalNaoVisualizadas.value = Math.max(0, totalNaoVisualizadas.value - 1)
    } catch (error) {
      console.error('Erro ao marcar notificação como visualizada:', error)
    }
  }
  dropdownAberto.value = false
  emit('abrir-sugestao', { idSugestaoMelhoria: notif.idSugestaoMelhoria, idComentario: notif.idComentario })
}

const marcarTodasVisualizadas = async () => {
  try {
    await api.put('/SugestoesMelhoria/notificacoes/visualizar-todas')
    notificacoes.value.forEach(n => { n.visualizada = true })
    totalNaoVisualizadas.value = 0
  } catch (error) {
    console.error('Erro ao marcar todas como visualizadas:', error)
  }
}

const getNotifIcon = (tipo) => {
  const icons = {
    feedback: 'bi-chat-square-quote',
    voto: 'bi-hand-thumbs-up',
    comentario: 'bi-chat',
    mention: 'bi-at'
  }
  return icons[tipo] || 'bi-bell'
}

const timeAgo = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now - date
  const diffMin = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffMin < 1) return 'agora'
  if (diffMin < 60) return `${diffMin}m`
  if (diffHours < 24) return `${diffHours}h`
  if (diffDays < 7) return `${diffDays}d`
  return date.toLocaleDateString('pt-BR')
}

const fecharDropdown = (e) => {
  if (dropdownAberto.value && bellRef.value && !bellRef.value.contains(e.target)) {
    dropdownAberto.value = false
  }
}

const onResizeOrScroll = () => {
  if (dropdownAberto.value) {
    calcularPosicao()
  }
}

const iniciarPolling = () => {
  pollingTimer = setInterval(async () => {
    try {
      const response = await api.get('/SugestoesMelhoria/notificacoes', {
        params: { page: 1, pageSize: 1 }
      })
      const data = response.data
      totalNaoVisualizadas.value = data.totalNaoVisualizadas || 0
    } catch {
      // Silencia erro de polling
    }
  }, 30000)
}

onMounted(() => {
  carregarNotificacoes(1)
  iniciarPolling()
  document.addEventListener('click', fecharDropdown)
  window.addEventListener('resize', onResizeOrScroll)
  window.addEventListener('scroll', onResizeOrScroll, { capture: true, passive: true })
})

onUnmounted(() => {
  if (pollingTimer) clearInterval(pollingTimer)
  document.removeEventListener('click', fecharDropdown)
  window.removeEventListener('resize', onResizeOrScroll)
  window.removeEventListener('scroll', onResizeOrScroll, { capture: true })
  liberarZIndex(tokenZIndex.value)
  tokenZIndex.value = null
})
</script>

<style scoped>
.bell-sugestoes {
  display: inline-flex;
  flex-shrink: 0;
}

.bell-btn {
  font-size: 1.15rem;
  width: 38px;
  height: 38px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  flex-shrink: 0;
}

.bell-badge {
  position: absolute;
  top: -4px;
  right: -6px;
  background: #DC2626;
  color: white;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 0.1rem 0.35rem;
  border-radius: 999px;
  min-width: 16px;
  text-align: center;
  line-height: 1.2;
  border: 2px solid var(--bs-body-bg);
}

.bell-dropdown {
  width: min(360px, calc(100vw - 16px));
  max-height: 480px;
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color-translucent);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.bell-dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--bs-border-color-translucent);
  font-size: 0.85rem;
}

.bell-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2rem 1rem;
  color: var(--bs-secondary-color);
  font-size: 0.85rem;
}

.bell-empty i {
  font-size: 2rem;
  opacity: 0.4;
}

.bell-loading {
  display: flex;
  justify-content: center;
  padding: 2rem;
}

.bell-list {
  flex: 1;
  overflow-y: auto;
  max-height: 360px;
}

.bell-item {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  width: 100%;
  padding: 0.65rem 1rem;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;
  border-bottom: 1px solid var(--bs-border-color-translucent);
}

.bell-item:last-child {
  border-bottom: none;
}

.bell-item:hover {
  background: var(--bs-tertiary-bg);
}

.bell-item-nao-lida {
  background: rgba(139, 92, 246, 0.04);
}

.bell-item-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: var(--bs-tertiary-bg);
  color: var(--bs-secondary-color);
  font-size: 0.8rem;
}

.bell-item-nao-lida .bell-item-icon {
  background: rgba(139, 92, 246, 0.12);
  color: var(--feed-primary, #8B5CF6);
}

.bell-item-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.bell-item-mensagem {
  font-size: 0.78rem;
  color: var(--bs-body-color);
  line-height: 1.3;
}

.bell-item-titulo {
  font-size: 0.7rem;
  color: var(--bs-secondary-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bell-item-time {
  font-size: 0.6rem;
  color: var(--bs-tertiary-color);
  margin-top: 0.1rem;
}

.bell-item-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--feed-primary, #8B5CF6);
  flex-shrink: 0;
  margin-top: 0.3rem;
}

.bell-ver-mais {
  text-align: center;
  padding: 0.5rem;
  border-top: 1px solid var(--bs-border-color-translucent);
}

.bell-ver-mais .btn-link {
  font-size: 0.8rem;
  color: var(--feed-primary, #8B5CF6);
  text-decoration: none;
}

.bell-ver-mais .btn-link:hover {
  text-decoration: underline;
}
</style>
